'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { User } from '@/core/domain';
import type { GetUserByIdUseCase } from '../../domain/usecases/get-user-by-id';
import type { GetUserByIdOptions } from '../../domain/repositories/user-repository';
import type { GetAllUsersUseCase } from '../../domain/usecases/get-all-users';
import type { CreateUserUseCase } from '../../domain/usecases/create-user';
import type { UpdateUserUseCase } from '../../domain/usecases/update-user';
import type { DeleteUserUseCase } from '../../domain/usecases/delete-user';

export interface UserState {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
}

export interface CreateUserResult {
  user: User | null;
  error: string | null;
}

export interface UserEvents {
  getUserById: (id: string, options?: GetUserByIdOptions) => Promise<User | null>;
  /** Lista todos os usuários. `forceRefresh` ignora cache local da lista. */
  getAllUsers: (options?: { forceRefresh?: boolean }) => Promise<User[]>;
  createUser: (user: User) => Promise<CreateUserResult>;
  updateUser: (id: string, data: Partial<User>) => Promise<User | null>;
  deleteUser: (id: string) => Promise<void>;
  clearError: () => void;
  clearCurrentUser: () => void;
}

export interface UserContextValue {
  state: UserState;
  events: UserEvents;
}

export interface UserProviderProps {
  children: React.ReactNode;
  getUserByIdUseCase: GetUserByIdUseCase;
  getAllUsersUseCase: GetAllUsersUseCase;
  createUserUseCase: CreateUserUseCase;
  updateUserUseCase: UpdateUserUseCase;
  deleteUserUseCase: DeleteUserUseCase;
}

const initialState: UserState = {
  currentUser: null,
  loading: false,
  error: null,
};

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({
  children,
  getUserByIdUseCase,
  getAllUsersUseCase,
  createUserUseCase,
  updateUserUseCase,
  deleteUserUseCase,
}: UserProviderProps) {
  const [state, setState] = useState<UserState>(initialState);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const clearCurrentUser = useCallback(() => {
    setState((s) => ({ ...s, currentUser: null }));
  }, []);

  const getUserById = useCallback(
    async (id: string, options?: GetUserByIdOptions): Promise<User | null> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const user = await getUserByIdUseCase.execute(id, options);
        setState((s) => ({ ...s, currentUser: user }));
        return user;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: e instanceof Error ? e.message : String(e),
          currentUser: null,
        }));
        return null;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserByIdUseCase]
  );

  const getAllUsers = useCallback(
    (options?: { forceRefresh?: boolean }) => getAllUsersUseCase.execute(options),
    [getAllUsersUseCase]
  );

  const createUser = useCallback(
    async (user: User): Promise<CreateUserResult> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const created = await createUserUseCase.execute(user);
        setState((s) => ({ ...s, currentUser: created }));
        return { user: created, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage }));
        return { user: null, error: errorMessage };
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [createUserUseCase]
  );

  const updateUser = useCallback(
    async (id: string, data: Partial<User>): Promise<User | null> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const updated = await updateUserUseCase.execute(id, data);
        setState((s) => ({ ...s, currentUser: updated }));
        return updated;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: e instanceof Error ? e.message : String(e),
        }));
        return null;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [updateUserUseCase]
  );

  const deleteUser = useCallback(
    async (id: string) => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await deleteUserUseCase.execute(id);
        setState((s) => ({ ...s, currentUser: null }));
      } catch (e) {
        setState((s) => ({
          ...s,
          error: e instanceof Error ? e.message : String(e),
        }));
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [deleteUserUseCase]
  );

  const events: UserEvents = useMemo(
    () => ({
      getUserById,
      getAllUsers,
      createUser,
      updateUser,
      deleteUser,
      clearError,
      clearCurrentUser,
    }),
    [
      getUserById,
      getAllUsers,
      createUser,
      updateUser,
      deleteUser,
      clearError,
      clearCurrentUser,
    ]
  );

  const value: UserContextValue = useMemo(
    () => ({ state, events }),
    [state, events]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used within UserProvider');
  }
  return ctx;
}
